// Boleto avulso da Gran — cobrança de cliente PRÓPRIO da parceira, fora da
// parceria com a Vigor (pedido do Financeiro em 22/09/2026: cliente da usina do
// dono da Gran, sem nada a ver com a Vigor).
//
// O boleto sai na conta Asaas da Gran e é IGUAL ao boleto único dela (pedido de
// 23/09/2026): a conta da Equatorial + a energia injetada com o desconto do cliente.
// Quando o cliente paga, o sistema paga a Equatorial pela conta da Gran e o resto
// fica com ela; a Vigor recebe zero.
//
// Os dados de energia vêm da própria conta da Equatorial: o operador sobe o PDF e a
// extração determinística (a mesma do robô, `extrair-fatura` + mapeador-faturas)
// preenche consumo, injeção, tarifas, leituras, saldo e a linha digitável. O
// operador informa só o desconto e o vencimento. A fatura nasce com
// `fora_parceria_vigor = true`, que é o que:
//   - faz a asaas-cobranca emitir SEM split (calcularCobrancaForaParceria);
//   - faz o gatilho pagar a Equatorial sem depender do cadastro da UC;
//   - tira a fatura da régua de WhatsApp, da fila de cobrança humana e dos números
//     da Vigor.
// Ver supabase/migrations/20260922194850_fatura_fora_parceria_vigor.sql.
//
// Avulso de cliente DA parceria não sai por aqui: a parte da Vigor depende do
// cálculo da parceria, que só a emissão normal (BoletoEmissionModal) faz.

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Ticket, AlertTriangle, CheckCircle, Copy, ExternalLink, Info, FileText, Loader2 } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { emitirCobrancaAvulsa } from '../../src/lib/emitirCobrancaAvulsa';
import { gerarBoletoGran } from '../../src/lib/gatewayResolver';
import { calcularCobrancaForaParceria, calcularParteParceiraPorInjecao } from '../../src/lib/splitParceira';
import { extrairFatura } from '../../src/services/extracaoApi';
import { mapearPayloadFaturas, type PayloadFaturas } from '../../supabase/functions/_shared/mapeador-faturas/index.ts';
import type { FaturaExtraida } from '../../supabase/functions/_shared/fatura-parser/types.ts';
import { validaLinhaDigitavel, valorDaLinhaDigitavel } from '../../supabase/functions/_shared/fatura-parser/boleto-pix.ts';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import { addDaysISO } from '../../src/lib/janelaCobranca';
import { situacaoDocumento } from '../../src/lib/pagadorBoleto';
import { normalizeUcCodigo } from '../../src/lib/ucCodigo';
import { normalizeMesReferencia, isMesReferenciaCanonico } from '../../src/lib/mesReferencia';
import { propsFundoModal } from '../../src/lib/backdropModal';
import { conferirCobrancaNoMes } from '../../src/lib/cobrancaNoMesmoMes';

interface BoletoAvulsoGranModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

interface EmpresaAsaas {
    id: string;
    nome: string;
}

interface Emitido {
    faturaId: number;
    numCobranca: string | null;
    linhaDigitavel: string | null;
    /** Link do Asaas — fallback quando o PDF da Gran não sai. */
    url: string | null;
    /** PDF no visual do boleto único da Gran, publicado no site institucional. */
    urlPdfGran: string | null;
    /** Por que o PDF da Gran não saiu (a cobrança existe mesmo assim). */
    avisoPdf: string | null;
}

const FORM_VAZIO = {
    uc_codigo: '',
    nome: '',
    cpf_cnpj: '',
    end_logradouro: '',
    end_numero: '',
    end_bairro: '',
    end_cep: '',
    end_cidade: '',
    end_uf: '',
    mes_referencia: '',
    vencimento_boleto: '',
    data_limite_pagamento_boleto: '',
    linha_equatorial: '',
    valor_equatorial: 0,
    consumo_kwh: 0,
    injetada_kwh: 0,
    /** Consumo não compensado, com tributos (R$/kWh). */
    tarifa_distribuidora: 0,
    /** Bandeira, com tributos (R$/kWh). */
    tarifa_bandeira: 0,
    /** Desconto do cliente em pontos percentuais (20 = 20%). */
    desconto_pct: 0,
};

type CampoForm = keyof typeof FORM_VAZIO;

const OBRIGATORIOS: Array<[CampoForm, string]> = [
    ['uc_codigo', 'UC'],
    ['nome', 'nome do cliente'],
    ['cpf_cnpj', 'CPF/CNPJ'],
    ['end_logradouro', 'logradouro'],
    ['end_numero', 'número'],
    ['end_bairro', 'bairro'],
    ['end_cep', 'CEP'],
    ['end_cidade', 'cidade'],
    ['end_uf', 'UF'],
    ['mes_referencia', 'mês de referência'],
    ['vencimento_boleto', 'vencimento'],
];

// Campos da conta que vão para a fatura como a extração leu, igual ao que o robô
// grava numa fatura comum. Lista fechada de propósito: campo novo no mapeador não
// pode quebrar o INSERT deste modal.
const CAMPOS_DA_CONTA = [
    'tipo_ligacao', 'data_apresentacao', 'data_vencimento',
    'leitura_anterior', 'leitura_atual', 'proxima_leitura',
    'saldo_acumulado_kwh', 'saldo_ugs_vigor', 'credito_recebido', 'geracao_ciclo', 'cadastro_rateio',
    'tarifa_s_imp_consumo_nao_comp', 'tarifa_consumo_s_imposto', 'consumo_scee_tarifa_c_imposto',
    'ilum_publica', 'energia_nao_isenta', 'outros_multas', 'icms', 'pis', 'cofins',
    'numero_medidor', 'pix_copia_cola_equatorial', 'debito_automatico_equatorial',
] as const;

const formatBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatKwh = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kWh`;
const formatData = (iso: unknown) => {
    const s = String(iso ?? '');
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10).split('-').reverse().join('/') : '—';
};
const texto = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const numero = (v: unknown) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
};

const inputCls = 'w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none';
const labelCls = 'block text-xs font-semibold text-gray-500 mb-1';

/** Campo de dinheiro com a mesma máscara do boleto avulso da Vigor (digita centavos). */
const CampoValor: React.FC<{ valor: number; onChange: (v: number) => void }> = ({ valor, onChange }) => (
    <div className="relative">
        <span className="absolute left-3 top-2 text-sm font-bold text-gray-400">R$</span>
        <input
            type="text"
            inputMode="numeric"
            value={Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            onChange={e => {
                const centavos = parseFloat(e.target.value.replace(/\D/g, '')) / 100;
                onChange(isNaN(centavos) ? 0 : centavos);
            }}
            className={`${inputCls} pl-10 font-semibold`}
        />
    </div>
);

/** Número com casas decimais livres (kWh, tarifa com 6 casas, desconto). */
const CampoNumero: React.FC<{ valor: number; onChange: (v: number) => void; passo: string; sufixo?: string }> = ({ valor, onChange, passo, sufixo }) => (
    <div className="relative">
        <input
            type="number"
            step={passo}
            min={0}
            value={Number.isFinite(valor) ? valor : 0}
            onChange={e => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
            className={`${inputCls} ${sufixo ? 'pr-12' : ''}`}
        />
        {sufixo && <span className="absolute right-3 top-2 text-sm text-gray-400">{sufixo}</span>}
    </div>
);

/** Campo só-leitura com botão de copiar (e de abrir, para links). */
const LinhaCopiavel: React.FC<{ rotulo: string; valor: string; onCopiar: (valor: string, rotulo: string) => void; mono?: boolean; abrir?: boolean }> = ({ rotulo, valor, onCopiar, mono, abrir }) => (
    <div>
        <label className={labelCls}>{rotulo}</label>
        <div className="flex gap-2">
            <input readOnly value={valor} className={`${inputCls} ${mono ? 'font-mono' : ''}`} />
            <button onClick={() => onCopiar(valor, rotulo)} className="px-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300" title="Copiar">
                <Copy size={16} />
            </button>
            {abrir && (
                <a href={valor} target="_blank" rel="noopener noreferrer" className="px-3 flex items-center rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300" title="Abrir">
                    <ExternalLink size={16} />
                </a>
            )}
        </div>
    </div>
);

const BoletoAvulsoGranModal: React.FC<BoletoAvulsoGranModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { addToast } = useToast();
    const { user, profile } = useAuth();
    const isAdm = profile?.role === 'adm';

    const [clienteProprio, setClienteProprio] = useState(true);
    const [form, setForm] = useState(FORM_VAZIO);
    const [limiteEditado, setLimiteEditado] = useState(false);
    const [empresas, setEmpresas] = useState<EmpresaAsaas[] | null>(null);
    const [empresaId, setEmpresaId] = useState('');
    const [emitirEmSandbox, setEmitirEmSandbox] = useState(false);
    const [avisoUcCadastrada, setAvisoUcCadastrada] = useState<string | null>(null);
    // Outra cobrança da mesma UC no mesmo mês. Aqui é só o aviso adiantado: quem
    // barra é a consulta nova feita no clique de Emitir.
    const [cobrancaNoMes, setCobrancaNoMes] = useState<string | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [confirmouDuplicata, setConfirmouDuplicata] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [emitido, setEmitido] = useState<Emitido | null>(null);
    // A conta da Equatorial: vai anexa ao boleto (com a marca d'água, como no boleto
    // único) e é de onde saem os dados de energia.
    const [pdfEquatorial, setPdfEquatorial] = useState<File | null>(null);
    const [extraido, setExtraido] = useState<PayloadFaturas | null>(null);
    const [lendoConta, setLendoConta] = useState(false);
    const [avisoLeitura, setAvisoLeitura] = useState<string | null>(null);

    // Estado limpo a cada abertura: sobra de uma emissão anterior não pode virar
    // cobrança de outro cliente.
    useEffect(() => {
        if (!isOpen) return;
        setClienteProprio(true);
        setForm(FORM_VAZIO);
        setLimiteEditado(false);
        setEmitirEmSandbox(false);
        setAvisoUcCadastrada(null);
        setCobrancaNoMes(null);
        setErro(null);
        setConfirmouDuplicata(false);
        setEmitido(null);
        setEmpresas(null);
        setPdfEquatorial(null);
        setExtraido(null);
        setLendoConta(false);
        setAvisoLeitura(null);

        // Por RPC, não por select: o RLS de empresas_parceiras só abre para adm e
        // Faturamento/Financeiro, e quem tem a permissão de cobrança (Pós Venda)
        // também emite. A função devolve só id e nome, e só para quem pode emitir.
        // Lista vazia = perfil sem acesso, e a tela diz isso em vez de emitir no escuro.
        supabase
            .rpc('parceiras_boleto_avulso')
            .then(({ data, error }) => {
                if (error) console.error('Erro ao carregar empresas parceiras:', error.message);
                const lista = (data || []) as EmpresaAsaas[];
                setEmpresas(lista);
                setEmpresaId(lista.length === 1 ? lista[0].id : '');
            });
    }, [isOpen]);

    const set = (campo: CampoForm, valor: string | number) => {
        setErro(null);
        setConfirmouDuplicata(false);
        setForm(prev => {
            const novo = { ...prev, [campo]: valor };
            // Enquanto não editada à mão, a data limite acompanha vencimento + 30.
            if (campo === 'vencimento_boleto' && !limiteEditado) {
                novo.data_limite_pagamento_boleto = addDaysISO(valor as string, 30);
            }
            // A linha da Equatorial carrega o valor da conta: é ele que o Asaas paga,
            // então é ele que a Gran tem que reter. Preenche sozinho quando dá.
            if (campo === 'linha_equatorial') {
                const doCodigo = valorDaLinhaDigitavel(String(valor).replace(/\D/g, ''));
                if (doCodigo != null) novo.valor_equatorial = doCodigo;
            }
            return novo;
        });
        if (campo === 'data_limite_pagamento_boleto') setLimiteEditado(true);
        if (campo === 'uc_codigo') setAvisoUcCadastrada(null);
        if (campo === 'uc_codigo' || campo === 'mes_referencia') setCobrancaNoMes(null);
    };

    // Outra cobrança da mesma UC no mesmo mês (Vigor ou parceria) — ver
    // src/lib/cobrancaNoMesmoMes.ts. Os avulsos fora da parceria ficam de fora: a
    // retentativa deles já é pega em `emitir`.
    const buscarCobrancaNoMes = (codigo: string, mes: string) => conferirCobrancaNoMes(supabase, codigo, mes, false);

    // Número de UC que já está na base quase sempre é cliente da parceria (ou da
    // Vigor) — aí esta opção está errada. Avisa, não bloqueia. UC marcada como
    // cliente próprio da Gran (`fora_parceria_vigor`) é o caso certo e não avisa. Já
    // cobrança no mesmo mês bloqueia (no clique de Emitir); aqui só aparece antes.
    const conferirUc = async (codigo: string, mes: string) => {
        if (codigo.replace(/\D/g, '').length < 5) return;
        const [{ data }, noMes] = await Promise.all([
            supabase.rpc('resolver_uc_por_codigo', { p_codigo: codigo }),
            buscarCobrancaNoMes(codigo, mes).catch(() => null),
        ]);
        const uc = (Array.isArray(data) ? data[0] : data) as { nome_cliente?: string | null; fora_parceria_vigor?: boolean | null } | null;
        setAvisoUcCadastrada(uc && !uc.fora_parceria_vigor
            ? `Esta UC já está cadastrada na base${uc.nome_cliente ? ` (${uc.nome_cliente})` : ''}. Se ela é cliente da parceria Vigor + Gran, não use esta opção.`
            : null);
        setCobrancaNoMes(noMes);
    };

    // UC marcada como cliente próprio da Gran: a conta que o robô trouxe para a fila da
    // Vigor no mesmo mês não vai virar cobrança (o cálculo é barrado), então sai da fila
    // assim que o avulso da Gran existe. Só pega o que ainda não tem boleto. Falhar
    // aqui não desfaz nada: devolve o aviso para a pessoa excluir à mão.
    const tirarContaDaFilaDaVigor = async (faturaGranId: number): Promise<string | null> => {
        const { data } = await supabase.rpc('resolver_uc_por_codigo', { p_codigo: form.uc_codigo });
        const uc = (Array.isArray(data) ? data[0] : data) as { fora_parceria_vigor?: boolean | null } | null;
        if (!uc?.fora_parceria_vigor) return null;
        const mes = normalizeMesReferencia(form.mes_referencia);
        const { data: excluidas, error } = await supabase
            .from('faturas')
            .update({
                status_fatura: 'Excluído',
                excluido_por_nome: profile?.full_name || user?.email || 'Sistema',
                excluido_em: new Date().toISOString(),
                motivo_exclusao: `Cliente próprio da Gran: ${mes} cobrado pelo boleto avulso da Gran (fatura ${faturaGranId}).`,
            })
            .eq('uc_codigo_norm', normalizeUcCodigo(form.uc_codigo))
            .eq('mes_referencia', mes)
            .eq('fora_parceria_vigor', false)
            .in('status_fatura', ['Aguardando processamento', 'Fatura disponível', 'Fatura validada'])
            .select('id');
        if (error) return `Não consegui tirar a conta de ${mes} da fila da Vigor (${error.message}). Exclua essa fatura na tela de Faturas.`;
        if (excluidas?.length) {
            await logAction(user?.id || 'SYSTEM', 'DELETE_INVOICE', {
                faturas: excluidas.map(f => f.id),
                motivo: `Cobrada pelo boleto avulso da Gran (fatura ${faturaGranId})`,
            });
        }
        return null;
    };

    // Lê a conta da Equatorial com a mesma extração do robô e preenche o formulário.
    // Falhar a leitura não trava: os campos continuam editáveis e o PDF ainda vai anexo.
    const lerConta = async (arquivo: File) => {
        setPdfEquatorial(arquivo);
        setExtraido(null);
        setAvisoLeitura(null);
        setErro(null);
        setLendoConta(true);
        try {
            const resposta = await extrairFatura(arquivo);
            if (!resposta.ok || !resposta.estruturado) {
                setAvisoLeitura(`Não consegui ler a conta (${resposta.error || 'sem dados'}). Preencha os campos de energia à mão.`);
                return;
            }
            const { payload } = mapearPayloadFaturas(resposta.estruturado as unknown as FaturaExtraida);
            setExtraido(payload);
            setForm(prev => ({
                ...prev,
                uc_codigo: texto(payload.uc_codigo) || prev.uc_codigo,
                nome: texto(payload.cliente_nome) || prev.nome,
                cpf_cnpj: texto(payload.cliente_cpf_cnpj) || prev.cpf_cnpj,
                end_logradouro: texto(payload.end_logradouro) || prev.end_logradouro,
                end_numero: texto(payload.end_numero) || prev.end_numero,
                end_bairro: texto(payload.end_bairro) || prev.end_bairro,
                end_cep: texto(payload.end_cep) || prev.end_cep,
                end_cidade: texto(payload.end_cidade) || prev.end_cidade,
                end_uf: texto(payload.end_uf) || prev.end_uf,
                mes_referencia: texto(payload.mes_referencia) || prev.mes_referencia,
                linha_equatorial: texto(payload.linha_digitavel_equatorial) || prev.linha_equatorial,
                valor_equatorial: numero(payload.valor_fatura_distribuidora),
                consumo_kwh: numero(payload.consumo_ativo_kwh),
                injetada_kwh: numero(payload.energia_injetada_gd1_kwh),
                tarifa_distribuidora: numero(payload.valor_tarifa_distribuidora),
                tarifa_bandeira: numero(payload.tarifa_bandeira),
            }));
            if (payload.tipo_pdf_fatura === 'espelho') {
                setAvisoLeitura('Este PDF é o espelho da conta, sem código de barras. Use a conta definitiva da Equatorial.');
            }
            if (payload.uc_codigo) conferirUc(String(payload.uc_codigo), texto(payload.mes_referencia));
        } finally {
            setLendoConta(false);
        }
    };

    const digitosLinha = form.linha_equatorial.replace(/\D/g, '');
    const valorLinha = valorDaLinhaDigitavel(digitosLinha);

    // Mesma conta que o PDF da Gran imprime (Energia injetada − Desconto do cliente).
    const parte = useMemo(() => calcularParteParceiraPorInjecao({
        injetadaKwh: form.injetada_kwh,
        tarifaDistribuidora: form.tarifa_distribuidora,
        tarifaBandeira: form.tarifa_bandeira,
        descontoPercentual: form.desconto_pct,
    }), [form.injetada_kwh, form.tarifa_distribuidora, form.tarifa_bandeira, form.desconto_pct]);
    const total = Math.round((form.valor_equatorial + parte.parteParceira) * 100) / 100;
    const debitoAutomatico = extraido?.debito_automatico_equatorial === true;

    // A mesma regra que a edge aplica antes de emitir — a tela só a mostra antes.
    const cobranca = useMemo(() => calcularCobrancaForaParceria({
        valorTotal: total,
        parteEquatorial: form.valor_equatorial,
        linhaDigitavelEquatorial: form.linha_equatorial,
        debitoAutomatico,
    }), [total, form.valor_equatorial, form.linha_equatorial, debitoAutomatico]);

    const dicaLinha = ((): { texto: string; ok: boolean | null } => {
        if (!digitosLinha) return { texto: 'Vem da conta da Equatorial. É com ela que o sistema paga a conta.', ok: null };
        if (digitosLinha.length !== 47 && digitosLinha.length !== 48) {
            return { texto: `${digitosLinha.length} dígitos — a linha tem 47 (boleto) ou 48 (conta de consumo).`, ok: false };
        }
        if (!validaLinhaDigitavel(digitosLinha)) return { texto: 'Dígito verificador não confere. Copie de novo da conta.', ok: false };
        if (valorLinha != null) return { texto: `Linha válida · cobra ${formatBRL(valorLinha)}`, ok: true };
        return { texto: 'Linha válida, sem valor embutido — confira o valor da conta.', ok: true };
    })();

    const validar = (): string | null => {
        if (!empresaId) return 'Escolha a empresa parceira que emite o boleto.';
        if (!pdfEquatorial) return 'Anexe o PDF da conta da Equatorial: é de lá que saem os dados de energia, e ela vai junto no boleto.';
        if (lendoConta) return 'Espere terminar a leitura da conta.';
        if (extraido?.tipo_pdf_fatura === 'espelho') return 'Este PDF é o espelho da conta, sem código de barras. Use a conta definitiva da Equatorial.';
        for (const [campo, rotulo] of OBRIGATORIOS) {
            if (!String(form[campo] ?? '').trim()) return `Preencha o campo ${rotulo}.`;
        }
        const doc = situacaoDocumento(form.cpf_cnpj);
        if (doc !== 'cpf' && doc !== 'cnpj') return `CPF/CNPJ do cliente é inválido: ${form.cpf_cnpj}.`;
        if (form.data_limite_pagamento_boleto && form.data_limite_pagamento_boleto < form.vencimento_boleto) {
            return 'A data limite de pagamento não pode ser anterior ao vencimento.';
        }
        if (form.desconto_pct < 0 || form.desconto_pct > 100) return 'O desconto do cliente precisa estar entre 0% e 100%.';
        if (cobranca.status === 'invalido') return cobranca.motivo || 'Confira os valores.';
        return null;
    };

    const pedirConfirmacao = async () => {
        const problema = validar();
        if (problema) {
            setErro(problema);
            return;
        }
        // Consulta nova, não o aviso guardado: o operador pode ter acabado de cancelar
        // a outra cobrança em outra aba.
        setLoading(true);
        try {
            const noMes = await buscarCobrancaNoMes(form.uc_codigo, form.mes_referencia);
            setCobrancaNoMes(noMes);
            if (noMes) {
                setErro(noMes);
                return;
            }
        } catch (e: any) {
            setErro(e?.message || 'Não consegui conferir se esta UC já tem cobrança no mês.');
            return;
        } finally {
            setLoading(false);
        }
        setShowConfirm(true);
    };

    const emitir = async () => {
        setShowConfirm(false);
        const problema = validar();
        if (problema) {
            setErro(problema);
            return;
        }
        setLoading(true);
        setErro(null);
        const sandbox = isAdm && emitirEmSandbox;
        try {
            // Retentativa: mesma UC + mesmo valor + mesmo vencimento há poucos minutos é
            // quase sempre o operador reemitindo porque a cobrança "não apareceu".
            const desde = new Date(Date.now() - 30 * 60_000).toISOString();
            const { data: recente } = await supabase
                .from('faturas')
                .select('id, num_cobranca_boleto')
                .eq('uc_codigo_norm', normalizeUcCodigo(form.uc_codigo))
                .eq('valor_emissao_boleto_unico', total)
                .eq('vencimento_boleto', form.vencimento_boleto)
                .not('status_fatura', 'in', '("Excluído","Cancelada","Aguardando Upload")')
                .gte('created_at', desde)
                .limit(1);
            if (recente && recente.length > 0 && !confirmouDuplicata) {
                setErro(
                    `Já existe uma cobrança igual criada há poucos minutos para esta UC (fatura ${recente[0].id}`
                    + `${recente[0].num_cobranca_boleto ? `, boleto ${recente[0].num_cobranca_boleto}` : ''}). `
                    + 'Clique em Emitir outra vez para criar assim mesmo.',
                );
                setConfirmouDuplicata(true);
                return;
            }

            // Sobe o PDF da Equatorial ANTES de criar a fatura: falhando aqui, nada foi
            // cobrado. Bucket privado `faturas`; pdf_fatura guarda o caminho, convenção
            // que a tela de Faturas, o e-mail de cobrança e o gerar-boleto-gran leem.
            const ucSeguro = form.uc_codigo.replace(/[^a-zA-Z0-9]/g, '') || 'sem-uc';
            const pdfFaturaPath = `avulso_parceira/${empresaId}/${Date.now()}_${ucSeguro}.pdf`;
            const { error: erroUpload } = await supabase.storage
                .from('faturas')
                .upload(pdfFaturaPath, pdfEquatorial as File, { contentType: 'application/pdf' });
            if (erroUpload) throw new Error('Não foi possível enviar o PDF da conta da Equatorial: ' + erroUpload.message);

            const daConta: Record<string, unknown> = {};
            for (const campo of CAMPOS_DA_CONTA) {
                if (extraido && extraido[campo] !== undefined) daConta[campo] = extraido[campo];
            }
            const fator = form.desconto_pct / 100;

            const { faturaId, fatura } = await emitirCobrancaAvulsa({
                emissor: 'asaas_parceira',
                sandbox,
                ucCodigo: form.uc_codigo,
                nomeQuem: profile?.full_name || user?.email || 'Sistema',
                campos: {
                    ...daConta,
                    empresa_parceira_id: empresaId,
                    fora_parceria_vigor: true,
                    ambiente: sandbox ? 'sandbox' : 'producao',
                    uc_codigo: form.uc_codigo.trim(),
                    cliente_nome: form.nome.trim(),
                    cliente_cpf_cnpj: form.cpf_cnpj,
                    nome_cliente_boleto: form.nome.trim(),
                    cpf_cnpj_cliente_boleto: form.cpf_cnpj,
                    end_logradouro: form.end_logradouro,
                    end_numero: form.end_numero,
                    end_bairro: form.end_bairro,
                    end_cep: form.end_cep,
                    end_cidade: form.end_cidade,
                    end_uf: form.end_uf,
                    // Canônico ('SET/2026'): a tela de Faturas filtra por igualdade exata.
                    mes_referencia: normalizeMesReferencia(form.mes_referencia),
                    vencimento_boleto: form.vencimento_boleto,
                    data_vencimento: daConta.data_vencimento ?? form.vencimento_boleto,
                    data_limite_pagamento_boleto: form.data_limite_pagamento_boleto || null,
                    // Energia: o que o PDF da Gran desenha (consumo, compensada, injetada,
                    // tarifa cheia = energia + bandeira, desconto do cliente).
                    consumo_ativo_kwh: form.consumo_kwh,
                    energia_injetada_gd1_kwh: form.injetada_kwh,
                    energia_injetada_gd2_kwh: 0,
                    qtd_injecao_scee_boleto: form.injetada_kwh,
                    valor_tarifa_distribuidora: form.tarifa_distribuidora,
                    tarifa_bandeira: form.tarifa_bandeira,
                    desconto_tarifa: fator,
                    desconto_bandeira: fator,
                    custo_s_vigor: parte.bruto,
                    economia: parte.desconto,
                    total_com_vigor: parte.parteParceira,
                    // Mesma convenção do boleto único: total = parte da empresa + Equatorial.
                    // Aqui a "parte da empresa" é da Gran — a Vigor não recebe nada.
                    valor_emissao_boleto_unico: total,
                    valor_fatura_distribuidora: form.valor_equatorial,
                    valor_boleto_vigor: parte.parteParceira,
                    linha_digitavel_equatorial: digitosLinha,
                    pdf_fatura: pdfFaturaPath,
                    tipo_pdf_fatura: 'definitiva',
                },
            });

            // PDF no visual do boleto único da Gran. A cobrança já existe no Asaas: se o
            // PDF falhar, o operador ainda tem o link do Asaas e pode regerar pela fatura.
            let urlPdfGran: string | null = null;
            let avisoPdf: string | null = null;
            try {
                const pdf = await gerarBoletoGran(faturaId);
                urlPdfGran = pdf?.url ?? null;
                if (pdf?.aviso) avisoPdf = pdf.aviso;
            } catch (e: any) {
                avisoPdf = `Cobrança criada, mas o PDF da Gran não foi gerado: ${e?.message || 'erro desconhecido'}. Use o link do Asaas ou regere pela fatura.`;
            }

            // Teste do sandbox não mexe na fila real.
            const avisoFila = sandbox ? null : await tirarContaDaFilaDaVigor(faturaId).catch((e: any) =>
                `Não consegui tirar a conta deste mês da fila da Vigor (${e?.message || 'erro desconhecido'}). Exclua essa fatura na tela de Faturas.`);
            if (avisoFila) avisoPdf = avisoPdf ? `${avisoPdf} ${avisoFila}` : avisoFila;

            await logAction(user?.id || 'SYSTEM', 'EMIT_BOLETO_AVULSO_GRAN', {
                fatura_id: faturaId,
                uc: form.uc_codigo,
                total,
                equatorial: form.valor_equatorial,
                parceira: parte.parteParceira,
                injetada_kwh: form.injetada_kwh,
                desconto_pct: form.desconto_pct,
                sandbox,
            });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'boletos', acao: 'emitir_boleto_avulso_gran', origem: 'acao' });

            // Não existe envio automático para este cliente (a régua da Vigor não fala
            // com ele): quem emite manda o PDF. Por isso o resultado fica na tela.
            setEmitido({
                faturaId,
                numCobranca: fatura?.num_cobranca_boleto ?? null,
                linhaDigitavel: fatura?.linha_digitavel_boleto ?? null,
                url: fatura?.url_boleto ?? null,
                urlPdfGran,
                avisoPdf,
            });
            addToast('Boleto da Gran emitido!', 'success');
            onSuccess?.();
        } catch (e: any) {
            console.error('Erro ao emitir boleto avulso da Gran:', e);
            setErro(e?.message || 'Erro inesperado ao emitir.');
        } finally {
            setLoading(false);
        }
    };

    const copiar = async (valor: string, rotulo: string) => {
        try {
            await navigator.clipboard.writeText(valor);
            addToast(`${rotulo} copiado.`, 'success');
        } catch {
            addToast('Não foi possível copiar. Selecione e copie à mão.', 'error');
        }
    };

    if (!isOpen) return null;

    const podeEmitir = clienteProprio && !!empresas?.length && !loading && !lendoConta;
    // Proporção da barra "para onde vai o dinheiro". Sem valor, fica cinza.
    const pctEquatorial = total > 0 ? (form.valor_equatorial / total) * 100 : 0;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-2 md:p-4" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-gray-800/95 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col border dark:border-gray-700" onClick={e => e.stopPropagation()}>

                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/40 shrink-0">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                            <Ticket className="text-secondary" />
                            Boleto avulso da Gran
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-9">
                            Sai na conta Asaas da Gran, igual ao boleto único dela.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                        <X size={24} className="text-gray-400" />
                    </button>
                </div>

                {emitido ? (
                    <div className="p-6 md:p-8 flex-1 overflow-y-auto space-y-5">
                        <div className="flex items-start gap-3 bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
                            <CheckCircle className="text-emerald-600 shrink-0 mt-0.5" size={22} />
                            <div className="text-sm text-emerald-900 dark:text-emerald-200">
                                <p className="font-bold">Boleto emitido na conta da Gran{emitirEmSandbox ? ' (teste)' : ''}.</p>
                                <p className="mt-1">
                                    Fatura {emitido.faturaId}{emitido.numCobranca ? ` · cobrança ${emitido.numCobranca}` : ''}.
                                    Nenhuma mensagem automática vai para este cliente: envie o PDF você mesmo.
                                </p>
                            </div>
                        </div>
                        {emitido.avisoPdf && (
                            <div className="flex gap-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
                                <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                                <p>{emitido.avisoPdf}</p>
                            </div>
                        )}
                        {emitido.urlPdfGran && <LinhaCopiavel rotulo="Boleto em PDF — é este que vai para o cliente" valor={emitido.urlPdfGran} onCopiar={copiar} abrir />}
                        {emitido.url && <LinhaCopiavel rotulo="Link do Asaas" valor={emitido.url} onCopiar={copiar} abrir />}
                        {emitido.linhaDigitavel && <LinhaCopiavel rotulo="Linha digitável" valor={emitido.linhaDigitavel} onCopiar={copiar} mono />}
                        <div className="flex justify-end">
                            <button onClick={onClose} className="px-5 py-2 rounded-lg bg-primary text-white font-semibold hover:bg-primary/90">
                                Fechar
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="p-6 md:p-8 flex-1 overflow-y-auto space-y-6">
                            {erro && (
                                <div className="bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 p-4 rounded-r-lg flex gap-3">
                                    <AlertTriangle className="text-red-500 shrink-0" size={20} />
                                    <p className="text-red-700 dark:text-red-300 text-sm">{erro}</p>
                                </div>
                            )}

                            {/* Tipo de cliente */}
                            <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${clienteProprio ? 'border-secondary/60 bg-secondary/10' : 'border-gray-200 dark:border-gray-700'}`}>
                                <input
                                    type="checkbox"
                                    checked={clienteProprio}
                                    onChange={e => { setClienteProprio(e.target.checked); setErro(null); }}
                                    className="mt-1 h-4 w-4 accent-secondary"
                                />
                                <div className="text-sm">
                                    <p className="font-bold text-gray-900 dark:text-white">Cliente próprio da Gran — fora da parceria com a Vigor</p>
                                    <p className="text-gray-600 dark:text-gray-400 mt-0.5">
                                        O boleto cobra a conta da Equatorial + a energia injetada com o desconto do cliente. O sistema paga a Equatorial e o resto fica com a Gran. A Vigor não recebe nada.
                                    </p>
                                </div>
                            </label>

                            {!clienteProprio ? (
                                <div className="flex gap-3 p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300">
                                    <Info size={18} className="shrink-0 mt-0.5 text-gray-400" />
                                    <p>
                                        Cliente da parceria Vigor + Gran não tem boleto avulso por aqui: a parte da Vigor depende do cálculo da parceria.
                                        Emita pela fatura, na tela de Faturas.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {empresas && empresas.length === 0 && (
                                        <div className="flex gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
                                            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                                            <p>Seu perfil não enxerga nenhuma empresa parceira com conta Asaas. Peça acesso a um administrador.</p>
                                        </div>
                                    )}
                                    {empresas && empresas.length > 1 && (
                                        <div>
                                            <label className={labelCls}>Empresa parceira *</label>
                                            <select value={empresaId} onChange={e => setEmpresaId(e.target.value)} className={inputCls}>
                                                <option value="">Escolha…</option>
                                                {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                                            </select>
                                        </div>
                                    )}

                                    {/* 1. Conta da Equatorial */}
                                    <section className="space-y-2">
                                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">1. Conta da Equatorial</h3>
                                        <label className="flex items-center gap-3 px-3 py-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                            {lendoConta
                                                ? <Loader2 size={18} className="text-secondary shrink-0 animate-spin" />
                                                : <FileText size={18} className="text-gray-400 shrink-0" />}
                                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                                {lendoConta
                                                    ? 'Lendo a conta…'
                                                    : pdfEquatorial ? pdfEquatorial.name : 'Escolher o PDF da conta da Equatorial *'}
                                            </span>
                                            <input
                                                type="file"
                                                accept="application/pdf"
                                                className="hidden"
                                                onChange={e => {
                                                    const arquivo = e.target.files?.[0] ?? null;
                                                    e.target.value = '';
                                                    if (!arquivo) return;
                                                    if (arquivo.type !== 'application/pdf') {
                                                        setErro('O arquivo da conta da Equatorial precisa ser PDF.');
                                                        return;
                                                    }
                                                    lerConta(arquivo);
                                                }}
                                            />
                                        </label>
                                        {extraido && !lendoConta && (
                                            <p className="text-xs text-emerald-600 dark:text-emerald-400">
                                                Conta lida: UC {texto(extraido.uc_codigo)} · {texto(extraido.mes_referencia)} · {formatBRL(numero(extraido.valor_fatura_distribuidora))}. Confira os campos abaixo.
                                            </p>
                                        )}
                                        {avisoLeitura && (
                                            <p className="flex items-start gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                                                <AlertTriangle size={13} className="shrink-0 mt-px" />
                                                <span>{avisoLeitura}</span>
                                            </p>
                                        )}
                                        {debitoAutomatico && (
                                            <p className="flex items-start gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                                                <AlertTriangle size={13} className="shrink-0 mt-px" />
                                                <span>Esta conta está em débito automático: a Equatorial vai debitar do cliente. Não dá para embutir no boleto.</span>
                                            </p>
                                        )}
                                        <p className="text-xs text-gray-400">
                                            Vai anexa ao boleto com a marca d'água "não realizar o pagamento", como no boleto único.
                                        </p>
                                    </section>

                                    {/* 2. Cliente */}
                                    <section className="space-y-4">
                                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">2. Cliente (pagador)</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className={labelCls}>UC na Equatorial *</label>
                                                <input value={form.uc_codigo} onChange={e => set('uc_codigo', e.target.value)} onBlur={() => conferirUc(form.uc_codigo, form.mes_referencia)} className={inputCls} />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className={labelCls}>Nome do cliente (pagador) *</label>
                                                <input value={form.nome} onChange={e => set('nome', e.target.value)} className={inputCls} />
                                            </div>
                                            {avisoUcCadastrada && (
                                                <p className="md:col-span-3 -mt-2 flex items-start gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                                                    <AlertTriangle size={13} className="shrink-0 mt-px" />
                                                    <span>{avisoUcCadastrada}</span>
                                                </p>
                                            )}
                                            {cobrancaNoMes && (
                                                <p className="md:col-span-3 -mt-2 flex items-start gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                                                    <AlertTriangle size={13} className="shrink-0 mt-px" />
                                                    <span>{cobrancaNoMes}</span>
                                                </p>
                                            )}
                                            <div>
                                                <label className={labelCls}>CPF/CNPJ *</label>
                                                <input value={form.cpf_cnpj} onChange={e => set('cpf_cnpj', e.target.value)} className={inputCls} />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className={labelCls}>Logradouro *</label>
                                                <input value={form.end_logradouro} onChange={e => set('end_logradouro', e.target.value)} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Número *</label>
                                                <input value={form.end_numero} onChange={e => set('end_numero', e.target.value)} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Bairro *</label>
                                                <input value={form.end_bairro} onChange={e => set('end_bairro', e.target.value)} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>CEP *</label>
                                                <input value={form.end_cep} onChange={e => set('end_cep', e.target.value)} className={inputCls} />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className={labelCls}>Cidade *</label>
                                                <input value={form.end_cidade} onChange={e => set('end_cidade', e.target.value)} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>UF *</label>
                                                <input value={form.end_uf} onChange={e => set('end_uf', e.target.value.toUpperCase().slice(0, 2))} className={inputCls} />
                                            </div>
                                        </div>
                                    </section>

                                    {/* 3. Energia */}
                                    <section className="space-y-4">
                                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">3. Energia (da conta)</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div>
                                                <label className={labelCls}>Consumo</label>
                                                <CampoNumero valor={form.consumo_kwh} onChange={v => set('consumo_kwh', v)} passo="0.01" sufixo="kWh" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Energia injetada</label>
                                                <CampoNumero valor={form.injetada_kwh} onChange={v => set('injetada_kwh', v)} passo="0.01" sufixo="kWh" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Tarifa energia (c/ tributos)</label>
                                                <CampoNumero valor={form.tarifa_distribuidora} onChange={v => set('tarifa_distribuidora', v)} passo="0.000001" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Tarifa bandeira (c/ tributos)</label>
                                                <CampoNumero valor={form.tarifa_bandeira} onChange={v => set('tarifa_bandeira', v)} passo="0.000001" />
                                            </div>
                                        </div>
                                        {extraido && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                Leituras: anterior {formatData(extraido.leitura_anterior)} · atual {formatData(extraido.leitura_atual)} · próxima {formatData(extraido.proxima_leitura)}
                                                {' · '}Saldo {formatKwh(numero(extraido.saldo_ugs_vigor))}
                                            </p>
                                        )}
                                    </section>

                                    {/* 4. Cobrança */}
                                    <section className="space-y-4">
                                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">4. Cobrança</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className={labelCls}>Desconto do cliente *</label>
                                                <CampoNumero valor={form.desconto_pct} onChange={v => set('desconto_pct', v)} passo="0.01" sufixo="%" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Vencimento *</label>
                                                <input type="date" value={form.vencimento_boleto} onChange={e => set('vencimento_boleto', e.target.value)} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Data limite de pagamento</label>
                                                <input type="date" min={form.vencimento_boleto || undefined} value={form.data_limite_pagamento_boleto} onChange={e => set('data_limite_pagamento_boleto', e.target.value)} className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Mês de referência *</label>
                                                <input placeholder="MM/AAAA" value={form.mes_referencia} onChange={e => set('mes_referencia', e.target.value)} onBlur={() => conferirUc(form.uc_codigo, form.mes_referencia)} className={inputCls} />
                                                {form.mes_referencia && !isMesReferenciaCanonico(form.mes_referencia) && (
                                                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Formato não reconhecido — use MM/AAAA.</p>
                                                )}
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className={labelCls}>Conta da Equatorial *</label>
                                                <CampoValor valor={form.valor_equatorial} onChange={v => set('valor_equatorial', v)} />
                                            </div>
                                            <div className="md:col-span-3">
                                                <label className={labelCls}>Linha digitável da conta da Equatorial *</label>
                                                <input value={form.linha_equatorial} onChange={e => set('linha_equatorial', e.target.value)} className={`${inputCls} font-mono`} />
                                                <p className={`mt-1 text-xs ${dicaLinha.ok === false ? 'text-red-600 dark:text-red-400' : dicaLinha.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
                                                    {dicaLinha.texto}
                                                </p>
                                            </div>
                                        </div>
                                    </section>

                                    {/* Composição do boleto — a mesma do detalhamento do PDF */}
                                    <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-sm">
                                        <p className="font-bold text-gray-900 dark:text-white mb-3">Composição do boleto</p>
                                        <div className="space-y-1.5">
                                            <p className="flex justify-between gap-4">
                                                <span className="text-gray-600 dark:text-gray-400">Energia injetada ({formatKwh(form.injetada_kwh)})</span>
                                                <span className="font-semibold tabular-nums dark:text-white">{formatBRL(parte.bruto)}</span>
                                            </p>
                                            <p className="flex justify-between gap-4">
                                                <span className="text-gray-600 dark:text-gray-400">Desconto do cliente ({form.desconto_pct.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%)</span>
                                                <span className="font-semibold tabular-nums text-secondary">−{formatBRL(parte.desconto)}</span>
                                            </p>
                                            <p className="flex justify-between gap-4">
                                                <span className="text-gray-600 dark:text-gray-400">Valor da fatura Equatorial</span>
                                                <span className="font-semibold tabular-nums dark:text-white">+{formatBRL(form.valor_equatorial)}</span>
                                            </p>
                                            <p className="flex justify-between gap-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                                                <span className="font-bold text-gray-900 dark:text-white">Total do boleto</span>
                                                <span className="font-bold tabular-nums dark:text-white">{formatBRL(total)}</span>
                                            </p>
                                        </div>

                                        <p className="font-bold text-gray-900 dark:text-white mt-5 mb-3">Quando o cliente pagar</p>
                                        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700 mb-3">
                                            <div className="bg-gray-500 dark:bg-gray-400 transition-all" style={{ width: `${pctEquatorial}%` }} />
                                            <div className="bg-secondary transition-all" style={{ width: `${total > 0 ? 100 - pctEquatorial : 0}%` }} />
                                        </div>
                                        <div className="space-y-1.5">
                                            <p className="flex justify-between gap-4">
                                                <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400"><span className="h-2 w-2 rounded-full bg-gray-500 dark:bg-gray-400" />Equatorial — o sistema paga pela conta da Gran</span>
                                                <span className="font-semibold tabular-nums dark:text-white">{formatBRL(form.valor_equatorial)}</span>
                                            </p>
                                            <p className="flex justify-between gap-4">
                                                <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400"><span className="h-2 w-2 rounded-full bg-secondary" />Fica com a Gran (menos a taxa do Asaas)</span>
                                                <span className="font-semibold tabular-nums dark:text-white">{formatBRL(parte.parteParceira)}</span>
                                            </p>
                                            <p className="flex justify-between gap-4">
                                                <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400"><span className="h-2 w-2 rounded-full border border-gray-300 dark:border-gray-600" />Vigor</span>
                                                <span className="font-semibold tabular-nums dark:text-white">{formatBRL(0)}</span>
                                            </p>
                                        </div>
                                        <p className="pt-3 text-xs text-gray-500 dark:text-gray-400">
                                            A régua de cobrança da Vigor não manda mensagem para este cliente, e a fatura não entra nos números da Vigor.
                                        </p>
                                        {cobranca.status === 'invalido' && total > 0 && digitosLinha && (
                                            <p className="pt-1 text-xs text-red-600 dark:text-red-400">{cobranca.motivo}</p>
                                        )}
                                    </section>

                                    {isAdm && (
                                        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                                            <input type="checkbox" checked={emitirEmSandbox} onChange={e => setEmitirEmSandbox(e.target.checked)} className="h-4 w-4" />
                                            Emitir em teste (sandbox do Asaas — nenhum dinheiro se move)
                                        </label>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-3 shrink-0">
                            <button onClick={onClose} className="px-5 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                                Cancelar
                            </button>
                            <button
                                onClick={pedirConfirmacao}
                                disabled={!podeEmitir}
                                className="px-5 py-2 rounded-lg bg-secondary text-white font-semibold hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Emitindo…' : 'Emitir boleto'}
                            </button>
                        </div>
                    </>
                )}
            </div>

            <ConfirmationModal
                isOpen={showConfirm}
                onClose={() => setShowConfirm(false)}
                onConfirm={emitir}
                title={emitirEmSandbox ? 'Emitir boleto de TESTE da Gran' : 'Emitir boleto na conta da Gran'}
                description={
                    <div className="text-left text-sm space-y-1">
                        <p><strong>Cliente:</strong> {form.nome} ({form.cpf_cnpj})</p>
                        <p><strong>UC:</strong> {form.uc_codigo} · <strong>Mês:</strong> {form.mes_referencia}</p>
                        <p><strong>Vencimento:</strong> {form.vencimento_boleto ? form.vencimento_boleto.split('-').reverse().join('/') : '—'}</p>
                        <p className="pt-2 text-base font-bold">Total: {formatBRL(total)}</p>
                        <p>Equatorial {formatBRL(form.valor_equatorial)} · Gran {formatBRL(parte.parteParceira)} · Vigor {formatBRL(0)}</p>
                    </div>
                }
                confirmText="Sim, emitir"
                cancelText="Revisar"
                isLoading={loading}
                variant="info"
            />
        </div>,
        document.body,
    );
};

export default BoletoAvulsoGranModal;
